import { graphql } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import React from 'react'

import * as styles from './bam-rewards.module.scss'
import BlueStackOverlay from '../../../assets/new-icons/blue-stack.svg'
import IntroBanner from '../../../components/banners/intro-banner/IntroBanner'
import PostCard from '../../../components/post-card/PostCard'
import RequestAccess from '../../../components/request-access/RequestAccess'
import CommencementSection from '../../../components/sections/commencementSection/CommencementSection'
import ReviewSection from '../../../components/sections/review-section/ReviewSection'
import SplitSection from '../../../components/sections/splitSection/SplitSection'
import TwinFeatureSection from '../../../components/sections/twin-feature-section/TwinFeatureSection'
import SEOControl from '../../../components/seoControl/seoControl'
import StoresBlock from '../../../components/storesBlock/StoresBlock'
import { SvgCheck } from '../../../components/ui/svg-icons/SvgCheck'
import WantToKnowMore from '../../../components/want-to-know-more/WantToKnowMore'
import { reviews } from '../../../constants/reviews'
import { colors } from '../../../constants/style'

const TITLE = 'Earn rewards while using the app | Bamboo BAM Rewards'
const DESCRIPTION =
  'BAM rewards is the first iteration of the BAM token implementation. Bamboo gives users the opportunity to earn BAM rewards based on in-app activity.'
const KEYWORDS =
  'bamboo, BAM, blockchain, gold, silver, precious metals, bitcoin, ethereum, digital assets, litecoin, crypto, buy, invest, invest today, micro investment, microinvestment, micro-investment, solvency protocol, proof of solvency, spare change, budget'
const IMAGE = 'https://www.getbamboo.io/assets/bam-token-gift.jpg'

const content = {
  firstSplitContent: {
    title: 'Join our rewards program and start earning.',
    text: 'We want to reward our users for using Bamboo. BAM is our rewards token that you can earn and cash out at any time.',
    renderedImage: (
      <StaticImage
        src="../../../assets/images/Bamboo_LifestyleShoot[JS]WEB-58.jpg"
        alt="Bamboo investments App"
        width={445}
        height={445}
      />
    ),
    renderedImageOverlay: (
      <img
        src={BlueStackOverlay}
        alt="Bitcoin Stack"
        className={styles.blueStackOverlay}
      />
    ),
    renderedImageOverlayClass: styles.overlay1,
    subTitle: 'BAM Rewards',
  },
  secondSplitContent: {
    subTitle: 'BAM Rewards',
    title: 'How to earn BAM Rewards',
    text: (
      <ul className={styles.list}>
        <li>
          <SvgCheck />
          Once you've activated the rewards program, all you need to do to
          continue earning is maintain 5% BAM in your portfolio.
        </li>
        <li>
          <SvgCheck />
          Earn 1.5 BAM for every top-up
        </li>
        <li>
          <SvgCheck />
          Earn 4 BAM per month with recurring payments turned on
        </li>
        <li>
          <SvgCheck />
          Earn 5 BAM for every time you refer a friend
        </li>
      </ul>
    ),
    renderedImage: (
      <StaticImage
        src="../../../assets/images/BAM_HowToEarn.png"
        alt="Bamboo investments App"
        width={540}
        height={540}
      />
    ),
  },
}

function BamRewardsPage({ data }) {
  const posts = data?.allMarkdownRemark?.edges || []

  const BannerImage = (
    <StaticImage
      src="../../../assets/images/BAM_Rewards.png"
      alt="Bamboo investments App"
      width={556}
      height={556}
    />
  )

  const EffortlessInvestingImage = (
    <StaticImage
      src="../../../assets/images/BAM_EffortlessInvesting.png"
      width={540}
      height={720}
    />
  )

  const blocks = posts.map(({ node }) => {
    const urlPath = node.frontmatter.path
      ? `/${node.frontmatter.path}/`
      : node.fields.slug

    return (
      <PostCard
        key={node.id}
        title={node.frontmatter.title}
        path={urlPath}
        image={node?.frontmatter?.postimage?.childImageSharp?.gatsbyImageData}
        pubDate={node.frontmatter.date}
        timeToRead={node.timeToRead}
      />
    )
  })

  return (
    <>
      <SEOControl
        title={TITLE}
        description={DESCRIPTION}
        keywords={KEYWORDS}
        image={IMAGE}
      />
      <IntroBanner
        title="Earn rewards simply by using the Bamboo app"
        subHeading="Earn BAM tokens every time you invest or refer a friend."
        backgroundImage={BannerImage}
        backgroundColor={colors.citrus50}
      />
      <SplitSection {...{ ...content.firstSplitContent }} />

      <IntroBanner
        heading="In the market for effortless investing?"
        subHeading="Start your investment journey with us."
        backgroundImage={EffortlessInvestingImage}
        appendItems={<StoresBlock className={styles.storeButtons} />}
        fullImageHeight
        backgroundColor={colors.tealA400}
        imageBottomOnMobile
      />
      <SplitSection {...{ ...content.secondSplitContent }} />
      <ReviewSection {...reviews[0]} />
      <CommencementSection horizontal />
      <RequestAccess />

      <TwinFeatureSection featuresList={['Rebalance', 'SMSF', 'Round-ups']} />
      <WantToKnowMore />
    </>
  )
}

export default BamRewardsPage

export const pageQuery = graphql`
  query {
    allMarkdownRemark(
      filter: {
        frontmatter: {
          layout: { eq: "post" }
          title: { in: ["2021 October Update", "Introducing BAM Rewards"] }
        }
      }
      sort: { fields: [frontmatter___date], order: DESC }
    ) {
      edges {
        node {
          id
          timeToRead
          fields {
            slug
          }
          frontmatter {
            date(formatString: "DD MMM")
            title
            postimage {
              childImageSharp {
                gatsbyImageData(
                  layout: FULL_WIDTH
                  aspectRatio: 1.36
                  placeholder: NONE
                  transformOptions: { fit: COVER }
                )
              }
            }
          }
        }
      }
    }
  }
`
